import React from 'react';

import { PROGRESS_REPORTS_SELECTED_CLASSROOM_ID, } from './progress_report_constants';

import DemoOnboardingTour, { DEMO_ONBOARDING_STUDENT_REPORTS_LANDING_PAGE, } from '../shared/demo_onboarding_tour';
import { getPageTitle, Spinner, SMALL_ICON_BASE_SRC, } from '../../../Shared'
import { requestGet, } from '../../../../modules/request'

const baseImgSrc = `${window.postbuildEnv.CDN_URL}/images/pages/all_reports`

const coreReportCards = [
  {
    title: 'Activity Summary Report',
    href: '/teachers/classrooms/scorebook',
    imgSrc: `${baseImgSrc}/activity_summary.svg`,
    bodyText: 'Quickly see which activities your students have completed and the skills that were demonstrated.',
  }, {
    title: 'Activity Analysis Report',
    href: '/teachers/progress_reports/diagnostic_reports#/activity_packs',
    imgSrc: `${baseImgSrc}/activity_analysis.svg`,
    bodyText: 'See how students responded to each question and get a clear analysis of the skills they demonstrated.',
  }
]

const diagnosticReportCards = [
  {
    title: 'My Diagnostics',
    href: '/teachers/progress_reports/diagnostic_reports/#/diagnostics',
    id: 'my-diagnostics-card',
    imgSrc: `${baseImgSrc}/my_diagnostics.svg`,
    bodyText: 'View results from your assigned diagnostics and get a personalized learning plan with recommended activities.',
  }, {
    title: 'Diagnostic Insights',
    href: '/teachers/progress_reports/diagnostic_insights',
    imgSrc: `${baseImgSrc}/diagnostic_insights.svg`,
    bodyText: 'Track students’ progress over multiple years and get recommendations for which diagnostic to assign next.',
    isNew: true,
  }
]

const premiumReportCards = [
  {
    title: 'Activity Scores Report',
    premium: true,
    href: '/teachers/progress_reports/activities_scores_by_classroom',
    imgSrc: `${baseImgSrc}/activity_scores.svg`,
    bodyText: 'View and download each student’s overall score and their individual scores per activity as a PDF or CSV.',
  }, {
    title: 'Concepts Report',
    premium: true,
    href: '/teachers/progress_reports/concepts/students',
    imgSrc: `${baseImgSrc}/concepts_report.svg`,
    bodyText: 'View an overall summary of how your students are performing across all writing and grammar concepts.',
  }, {
    title: 'Standards Report',
    premium: true,
    href: '/teachers/progress_reports/standards/classrooms',
    imgSrc: `${baseImgSrc}/standards_report.svg`,
    bodyText: 'View this report to see how your students are performing on specific National standards.',
  }, {
    title: 'Data Export',
    premium: true,
    href: '/teachers/progress_reports/activity_sessions',
    imgSrc: `${baseImgSrc}/data_export.svg`,
    bodyText: 'You can export the data as a CSV file by filtering for the classrooms, activity packs, or students you would like to export and then pressing “Download Report.”',
  }
]

const contentHubCards = (hasAssignedSocialStudiesActivities, hasAssignedScienceActivities, loading) => {
  const socialStudiesLink = hasAssignedSocialStudiesActivities ? '/teachers/progress_reports/social-studies/world-history-1200-to-present' : '/assign/social-studies'
  const socialStudiesBodyText = hasAssignedSocialStudiesActivities ? 'Your one-stop shop for assigning new social studies activities and tracking student\xa0progress.' : "Explore Quill's Social Studies Activities. Once assigned, return here to assign additional activities and track student\xa0progress."

  const interdisciplinaryScienceLink = hasAssignedScienceActivities ? '/teachers/progress_reports/interdisciplinary-science/building-ai-knowledge' : '/assign/interdisciplinary-science'
  const interdisciplinaryScienceBodyText = hasAssignedScienceActivities ? 'Your one-stop shop for assigning new interdisciplinary science activities and tracking student\xa0progress.' : "Explore Quill's Interdisciplinary Science Activities. Once assigned, return here to assign additional activities and track student\xa0progress."

  return [
    {
      title: 'Social Studies Dashboard',
      href: socialStudiesLink,
      imgSrc: `${baseImgSrc}/social_studies_dashboard.svg`,
      bodyText: socialStudiesBodyText,
      new: true,
      loading
    }, {
      title: 'Interdisciplinary Science Dashboard',
      href: interdisciplinaryScienceLink,
      imgSrc: `${baseImgSrc}/interdisciplinary_science_dashboard.svg`,
      bodyText: interdisciplinaryScienceBodyText,
      new: true,
      loading
    }
  ]
}

const Card = ({ isNew, premium, title, id, href, bodyText, imgSrc, loading, }) => {
  const newElement = isNew ? <span className="gold-new-tag">NEW</span> : null
  let linkClassName = 'quill-button focus-on-light contained medium '
  linkClassName += premium ? 'gold' : 'green'
  return (
    <section aria-labelledby={title} className={`report-card ${premium ? 'premium-card' : ''}`} id={id} key={title}>
      {newElement}
      <h3 id={title}>{title}</h3>
      <p>{loading ? <Spinner /> : bodyText}</p>
      <div className="button-and-img-wrapper">
        <a className={linkClassName} href={href}>View Report</a>
        <img alt="" src={imgSrc} />
      </div>
    </section>
  );
};

const ReportSection = ({ cards, imgSrc, title, }) => {
  const reportCardsClassName = `report-cards ${cards.length === 1 ? 'one-card' : ''}`
  return (
    <section aria-labelledby={title} className="report-section">
      <div className="img-and-title-wrapper">
        <img alt="" src={imgSrc} />
        <h2 id={title}>{title}</h2>
      </div>
      <section className={reportCardsClassName}>
        {cards.map(card => <Card key={card.title} {...card} />)}
      </section>
    </section>
  )
}


const LandingPage = ({ flag, }) => {
  document.title = getPageTitle("View Reports", "Landing Page")
  const [loadingContentHubData, setLoadingContentHubData] = React.useState(true)
  const [hasAssignedSocialStudiesActivities, setHasAssignedSocialStudiesActivities] = React.useState(false)
  const [hasAssignedScienceActivities, setHasAssignedScienceActivities] = React.useState(false)

  React.useEffect(() => {
    requestGet('/teachers/progress_reports/assigned_content_hub_activities_status',
      ({ has_assigned_science_activities, has_assigned_social_studies_activities, }) => {
        setHasAssignedScienceActivities(has_assigned_science_activities)
        setHasAssignedSocialStudiesActivities(has_assigned_social_studies_activities)
        setLoadingContentHubData(false)
      }
    )
  })

  return (
    <main className="progress-reports-landing-page">
      <DemoOnboardingTour pageKey={DEMO_ONBOARDING_STUDENT_REPORTS_LANDING_PAGE} />
      <h1>All Reports</h1>
      <ReportSection
        cards={coreReportCards}
        imgSrc={`${SMALL_ICON_BASE_SRC}/bar-chart.svg`}
        title='Core Reports'
      />
      <ReportSection
        cards={diagnosticReportCards}
        imgSrc={`${SMALL_ICON_BASE_SRC}/bar-graph-increasing.svg`}
        title='Diagnostic Reports'
      />
      <ReportSection
        cards={premiumReportCards}
        imgSrc={`${SMALL_ICON_BASE_SRC}/premium-diamond.svg`}
        title='Premium Reports'
      />
      <ReportSection
        cards={contentHubCards(hasAssignedSocialStudiesActivities, hasAssignedScienceActivities, loadingContentHubData)}
        imgSrc={`${SMALL_ICON_BASE_SRC}/graduation-cap.svg`}
        title='Curriculum-Specific Dashboards'
      />
    </main>
  );
}

export default LandingPage
